/* eslint-disable dot-notation */

/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useEffect, useState} from 'react';
import {
  FlatList,
  RefreshControl,
  InteractionManager,
  StyleSheet,
  View,
  useWindowDimensions,
  Platform,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {
  ActivityIndicator,
  Button,
  Card,
  IconButton,
  List,
  Text,
} from 'react-native-paper';
import {errorHandler, prepareDataForFlatList} from '../services/Utils';
import {theme} from '../theme/theme';
import moment from 'moment';
import {DatePickerModal} from 'react-native-paper-dates';
import CountersModal from '../components/CountersModal';
import {useDispatch, useSelector} from 'react-redux';
import {printItemWiseReport} from '../services/PrintService';
import * as BluetoothPrintService from '../services/BluetoothPrintService';
import {showToast} from '../store/info.slice';
import {ReportsApiService} from '../services/ReportsApiService';

const getNumColumns = width => {
  if (width < 500) {
    return 1;
  }
  if (width < 576) {
    return 2;
  }
  if (width < 768) {
    return 2;
  }
  if (width < 992) {
    return 3;
  }
  if (width < 1200) {
    return 4;
  }
  if (width < 1400) {
    return 5;
  }
  return 5;
};

/**
 * Main component for displaying item-wise sales reports.
 * Handles fetching, filtering, and paginating report data.
 * @param {object} navigation - Navigation prop for screen navigation
 */
function ItemWiseReport({navigation}) {
  const [interactionCompleted, setIntersectionCompleted] = useState(false);
  const {width} = useWindowDimensions();
  const dispatch = useDispatch();
  const canteens = useSelector(state => state.cafe.canteens);
  const customer = useSelector(state => state.customer?.customer);
  const numColumns = getNumColumns(width);
  const [report, setReport] = useState([]);
  const [counter, setCounter] = useState(null);
  const [status, setStatus] = useState('idle');
  const [setError] = useState(null);
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const LIMIT = 36;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [visibleCountersModal, setVisibleCountersModal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  /**
   * Handles dismissing the date picker modal.
   */
  const onDismissSingle = useCallback(() => {
    setOpenDatePicker(false);
  }, [setOpenDatePicker]);

  /**
   * Handles confirming a date selection from the date picker.
   * @param {object} params - Date picker params
   */
  const onConfirmSingle = React.useCallback(
    params => {
      setOpenDatePicker(false);
      setSelectedDate(params.date);
    },
    [setOpenDatePicker, setSelectedDate],
  );

  /**
   * Fetches item-wise report data from the server.
   */
  const fetchData = ({action = 'view', format = 'json'} = {}) => {
    setRefreshing(false);
    setStatus('loading');
    const params = {
      action,
      format,
      page,
      limit: LIMIT,
      fromDate: moment(selectedDate).format('YYYY-MM-DD'),
      toDate: moment(selectedDate).format('YYYY-MM-DD'),
      cafe_id: customer?.cafe_id,
    };
    if (counter) {
      params['canteen_id'] = counter['canteen_id'];
    }
    ReportsApiService.get('/cash-counter/item-wise', {params})
      .then(response => {
        if (action === 'download') {
          if (response.data?.length === 0) {
            return dispatch(showToast('No data available to print'));
          }
          const report_params = {
            report_name: 'Item Wise Report',
            cafe_name: customer?.cafe_name,
            login_at: customer?.logged_in_time
              ? moment(customer.logged_in_time).format('llll')
              : moment().format('llll'),
            canteen_name: counter
              ? counter?.canteen_name
              : canteens?.length === 1
              ? canteens[0]?.canteen_name
              : 'All',
            reporting_time: moment().format('llll'),
            items: response.data,
            customer_name: `${customer?.cust_name ?? ''} ${customer?.cust_last_name ?? ''}`.trim(),
          };
          if (Platform.OS === 'web') {
            printItemWiseReport(report_params);
          } else if (Platform.OS === 'android') {
            BluetoothPrintService?.printItemWiseReport(report_params);
          }
          return;
        }
        setStatus('succeeded');
        setRefreshing(false);
        let len = response.data['reports'].length;
        if (len > 0 && len >= LIMIT) {
          setLoadingMore(true);
        } else {
          setLoadingMore(false);
        }
        const temp =
          page === 1
            ? response.data['reports']
            : report.concat(response.data['reports']);
        setReport(
          prepareDataForFlatList({
            numColumns,
            key: 'item_id',
            page,
            data: temp,
          }),
        );
      })
      .catch(_error => {
        setStatus('failed');
        setRefreshing(false);
        setLoadingMore(false);
        setError(_error);
        errorHandler(_error);
      });
  };

  useEffect(() => {
    const task = InteractionManager.runAfterInteractions(async () => {
      setIntersectionCompleted(true);
    });
    return () => {
      task.cancel();
    };
  }, []);

  useEffect(() => {
    if (interactionCompleted && status !== 'loading') {
      fetchData();
    }
  }, [interactionCompleted, page, selectedDate, counter]);

  useEffect(() => {
    if (interactionCompleted) {
      resetReport();
    }
  }, [interactionCompleted, selectedDate]);

  /**
   * Resets the report state and pagination.
   */
  const resetReport = () => {
    setStatus('idle');
    setPage(1);
    setReport([]);
    fetchData();
  };

  /**
   * Renders a single item in the report list.
   * @param {object} param0 - Contains item and index
   */
  const renderItem = ({item, index}) => {
    if (item.isEmpty) {
      return <View style={{flex: 1}} />;
    }
    return (
      <View style={{margin: 1, flex: 1}}>
        <List.Item
          style={{backgroundColor: '#fff'}}
          title={item?.item_name}
          titleStyle={{fontSize: 12}}
          titleNumberOfLines={null}
          description={
            <View>
              <Text style={{fontSize: 12}}>Counter: {item?.counter}</Text>
              <Text style={{fontSize: 12}}>Total Qty: {item?.total_qty}</Text>
              <Text style={{fontSize: 12}}>
                Total Amount: {item?.total_amount}
              </Text>
            </View>
          }
        />
      </View>
    );
  };

  /**
   * Returns the RefreshControl component for pull-to-refresh.
   */
  const _refreshControl = () => {
    return (
      <RefreshControl
        colors={[theme.colors.primary]}
        refreshing={refreshing}
        onRefresh={() => _onRefresh()}
      />
    );
  };

  /**
   * Handles pull-to-refresh action.
   * @param {boolean} retry - Optional retry flag
   */
  const _onRefresh = retry => {
    resetReport();
  };

  /**
   * Handles loading more data when the end of the list is reached.
   */
  const _handleLoadMore = () => {
    if (loadingMore && status !== 'loading' && report.length > 0) {
      setStatus('idle');
      setPage(_p => _p + 1);
    }
  };

  /**
   * Renders the footer component for the list (loading spinner).
   */
  const ListFooterComponent = () => {
    if (!loadingMore) {
      return null;
    }
    return (
      <View style={{padding: 16}}>
        <ActivityIndicator animating={true} color={theme.colors.primary} />
      </View>
    );
  };

  /**
   * Renders the header component for the list (filters, date, counter).
   */
  const ListHeaderComponent = () => {
    return (
      <Card
        elevation={0}
        style={{
          margin: 0,
          marginBottom: 2,
          padding: 0,
          borderRadius: 0,
          backgroundColor: '#ffffff',
        }}>
        <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
          <Card.Title
            style={{flex: 1}}
            titleStyle={{fontSize: 14}}
            subtitleStyle={{fontSize: 12}}
            title="Select Counter"
            subtitle={counter ? counter?.canteen_name : 'All'}
            right={props => (
              <IconButton
                icon={'chevron-down'}
                onPress={() => setVisibleCountersModal(t => !t)}
              />
            )}
          />
          <Card.Title
            style={{flex: 1}}
            titleStyle={{fontSize: 14}}
            subtitleStyle={{fontSize: 12}}
            title="Select Date"
            subtitle={moment(selectedDate).format('MMMM D, YYYY')}
            // right={props => (
            //   <IconButton
            //     icon={'calendar-range'}
            //     onPress={() => setOpenDatePicker(t => !t)}
            //   />
            // )}
          />
        </View>
      </Card>
    );
  };

  /**
   * Handles closing the counter selection modal.
   * @param {boolean} visible - Modal visibility
   */
  const onCounterModalClose = visible => {
    setVisibleCountersModal(visible);
  };

  /**
   * Handles selecting a counter from the modal.
   * @param {object} item - Selected counter
   */
  const onCounterSelect = item => {
    onCounterModalClose(false);
    setTimeout(() => {
      setStatus('idle');
      setPage(1);
      setReport([]);
      setCounter(item);
    }, 500);
  };

  return (
    <SafeAreaView edges={['bottom']} style={styles.container}>
      {interactionCompleted && (
        <React.Fragment>
          <View style={styles.mainContainer}>
            {/* {ListHeaderComponent()} */}
            <FlatList
              data={report}
              renderItem={renderItem}
              keyExtractor={(item, index) => (item?.item_id).toString()}
              onEndReached={_handleLoadMore}
              onEndReachedThreshold={0.1}
              key={numColumns}
              numColumns={numColumns}
              columnWrapperStyle={numColumns > 1 ? {gap: 1} : undefined} // RN 0.71+
              refreshControl={_refreshControl()}
              ListHeaderComponent={ListHeaderComponent}
              ListFooterComponent={ListFooterComponent}
            />
            <DatePickerModal
              locale="en"
              mode="single"
              validRange={{endDate: today}}
              visible={openDatePicker}
              onDismiss={onDismissSingle}
              date={selectedDate}
              onConfirm={onConfirmSingle}
              presentationStyle={'pageSheet'}
              saveLabel={'Submit'}
            />
            <CountersModal
              visible={visibleCountersModal}
              onCounterSelect={onCounterSelect}
              onCounterModalClose={onCounterModalClose}
            />
          </View>
          {customer?.customer_settings?.enable_printing_item_wise_report &&
            // Platform.OS !== 'web' && 
            (
              <View style={styles.footer}>
                <Button
                  mode="contained"
                  style={{margin: 4}}
                  onPress={() => fetchData({action: 'download'})}>
                  Print
                </Button>
              </View>
            )}
        </React.Fragment>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f1f1f3',
  },
  mainContainer: {
    flex: 1,
  },
  footer: {
    position: Platform.OS === 'web' ? 'sticky' : undefined,
    bottom: 0,
    width: '100%',
  },
});

export default ItemWiseReport;
